/**
 * JapanGraphic — culturally grounded SVG accents that complement the
 * abstract BrandGraphic set. Used to anchor pages in Japan / ETC /
 * mobility / records imagery without resorting to flag clichés.
 *
 * All variants are aria-hidden, decorative-only, and animated via the
 * existing tailwind `animate-brand-*` keyframes — so they fully respect
 * prefers-reduced-motion (the global guard in index.css disables them).
 *
 * Variants:
 *   - "torii"       Minimalist torii-gate silhouette (cultural anchor)
 *   - "fuji"        Stylized Mt. Fuji silhouette with cloud band
 *   - "seigaiha"    Repeating Japanese wave pattern (decorative bg)
 *   - "asanoha"     Hemp-leaf geometric pattern (decorative bg)
 *   - "shinkansen"  Streamlined bullet-train side profile w/ speed lines
 *   - "etc-gantry"  Highway gantry with ETC lane labels + cars passing
 *   - "etc-card"    Stylized ETC card with chip and animated read beam
 *   - "receipt"     Toll-receipt line items drawing in on scroll
 *   - "yen-coin"    Yen-mark coin with orbiting accent
 *   - "tatami-grid" Subtle tatami-mat grid background pattern
 */
const PAL = {
  navy: "#0F1D2D",
  blue: "#0E5FB0",
  purple: "#5B2E83",
  green: "#2F8F3C",
  sky: "#7AB8E8",
  fujiSnow: "#FFFFFF",
};

export default function JapanGraphic({ variant = "torii", color, className = "", ...rest }) {
  const stroke = color || PAL.navy;

  /* ───────── TORII ───────── */
  if (variant === "torii") {
    return (
      <svg viewBox="0 0 200 220" fill="none" className={className} aria-hidden="true" {...rest}>
        {/* Top kasagi (curved roof beam) */}
        <path d="M20 50 Q 100 30, 180 50 L 175 62 Q 100 45, 25 62 Z" fill={stroke} className="animate-brand-fade-in" />
        {/* Shimaki (second beam) */}
        <rect x="28" y="70" width="144" height="9" rx="1" fill={stroke} opacity="0.92" className="animate-brand-fade-in" style={{ animationDelay: "200ms" }} />
        {/* Nuki (cross-beam) */}
        <rect x="38" y="105" width="124" height="6" rx="1" fill={stroke} opacity="0.85" className="animate-brand-fade-in" style={{ animationDelay: "320ms" }} />
        {/* Left pillar */}
        <rect x="44" y="79" width="14" height="125" fill={stroke} className="animate-brand-fade-up" style={{ animationDelay: "100ms" }} />
        {/* Right pillar */}
        <rect x="142" y="79" width="14" height="125" fill={stroke} className="animate-brand-fade-up" style={{ animationDelay: "100ms" }} />
        {/* Ground line */}
        <line x1="20" y1="208" x2="180" y2="208" stroke={stroke} strokeWidth="1.5" opacity="0.4" />
      </svg>
    );
  }

  /* ───────── FUJI ───────── */
  if (variant === "fuji") {
    return (
      <svg viewBox="0 0 320 200" fill="none" className={className} aria-hidden="true" preserveAspectRatio="xMidYMid meet" {...rest}>
        {/* Mountain body */}
        <path
          d="M30 180 L 130 60 Q 160 38, 190 60 L 290 180 Z"
          fill={stroke}
          opacity="0.92"
          className="animate-brand-fade-up"
        />
        {/* Snow cap */}
        <path
          d="M115 78 Q 130 70, 138 75 Q 148 60, 160 56 Q 172 58, 182 73 Q 192 70, 205 78 L 195 92 Q 175 82, 160 90 Q 145 82, 125 92 Z"
          fill={PAL.fujiSnow}
          className="animate-brand-fade-in"
          style={{ animationDelay: "350ms" }}
        />
        {/* Cloud band */}
        <path
          d="M0 138 Q 60 124, 130 138 T 320 134"
          stroke={stroke}
          strokeWidth="1.5"
          opacity="0.35"
          fill="none"
          strokeDasharray="800"
          className="animate-brand-draw"
        />
        <path
          d="M0 152 Q 80 142, 160 152 T 320 148"
          stroke={stroke}
          strokeWidth="1"
          opacity="0.22"
          fill="none"
          strokeDasharray="800"
          className="animate-brand-draw"
          style={{ animationDelay: "320ms" }}
        />
        {/* Horizon */}
        <line x1="0" y1="180" x2="320" y2="180" stroke={stroke} strokeWidth="1" opacity="0.45" />
      </svg>
    );
  }

  /* ───────── SEIGAIHA (overlapping waves) ───────── */
  if (variant === "seigaiha") {
    return (
      <svg viewBox="0 0 600 240" fill="none" className={className} aria-hidden="true" preserveAspectRatio="xMidYMid slice" {...rest}>
        <defs>
          <pattern id="seigaiha-pat" x="0" y="0" width="60" height="30" patternUnits="userSpaceOnUse">
            <circle cx="30" cy="30" r="28" fill="none" stroke={stroke} strokeWidth="0.8" opacity="0.55" />
            <circle cx="30" cy="30" r="20" fill="none" stroke={stroke} strokeWidth="0.7" opacity="0.45" />
            <circle cx="30" cy="30" r="12" fill="none" stroke={stroke} strokeWidth="0.6" opacity="0.35" />
            <circle cx="0" cy="30" r="28" fill="none" stroke={stroke} strokeWidth="0.8" opacity="0.55" />
            <circle cx="60" cy="30" r="28" fill="none" stroke={stroke} strokeWidth="0.8" opacity="0.55" />
          </pattern>
          <linearGradient id="seigaiha-fade" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0" />
            <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0.95" />
          </linearGradient>
        </defs>
        <rect width="600" height="240" fill="url(#seigaiha-pat)" />
        <rect width="600" height="240" fill="url(#seigaiha-fade)" />
      </svg>
    );
  }

  /* ───────── ASANOHA (hemp-leaf geometric) ───────── */
  if (variant === "asanoha") {
    return (
      <svg viewBox="0 0 600 400" fill="none" className={className} aria-hidden="true" preserveAspectRatio="xMidYMid slice" {...rest}>
        <defs>
          <pattern id="asanoha-pat" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse">
            <g stroke={stroke} strokeWidth="0.7" opacity="0.32" fill="none">
              <line x1="30" y1="0" x2="30" y2="60" />
              <line x1="0" y1="30" x2="60" y2="30" />
              <line x1="0" y1="0" x2="60" y2="60" />
              <line x1="60" y1="0" x2="0" y2="60" />
              <line x1="30" y1="0" x2="60" y2="30" />
              <line x1="60" y1="30" x2="30" y2="60" />
              <line x1="30" y1="0" x2="0" y2="30" />
              <line x1="0" y1="30" x2="30" y2="60" />
            </g>
          </pattern>
          <radialGradient id="asanoha-fade" cx="50%" cy="50%" r="65%">
            <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0" />
            <stop offset="100%" stopColor="#FFFFFF" stopOpacity="1" />
          </radialGradient>
        </defs>
        <rect width="600" height="400" fill="url(#asanoha-pat)" />
        <rect width="600" height="400" fill="url(#asanoha-fade)" />
      </svg>
    );
  }

  /* ───────── SHINKANSEN ───────── */
  if (variant === "shinkansen") {
    return (
      <svg viewBox="0 0 400 140" fill="none" className={className} aria-hidden="true" {...rest}>
        {/* Speed lines behind */}
        {[0, 1, 2, 3].map((i) => (
          <line
            key={i}
            x1={0}
            x2={120 - i * 18}
            y1={80 + i * 5}
            y2={80 + i * 5}
            stroke={stroke}
            strokeWidth="1.2"
            opacity={0.35 - i * 0.06}
            strokeDasharray="180"
            className="animate-brand-draw"
            style={{ animationDelay: `${i * 100}ms` }}
          />
        ))}
        {/* Train body — long, streamlined nose pointing right */}
        <path
          d="M120 80 Q 200 50, 360 60 L 380 75 Q 360 90, 200 90 L 120 90 Q 110 85, 120 80 Z"
          fill={stroke}
          opacity="0.92"
          className="animate-brand-slide-right"
        />
        {/* Window strip */}
        <rect x="200" y="68" width="160" height="6" rx="2" fill={PAL.sky} opacity="0.65" className="animate-brand-fade-in" style={{ animationDelay: "260ms" }} />
        {/* Track */}
        <line x1="0" y1="110" x2="400" y2="110" stroke={stroke} strokeWidth="1" opacity="0.5" />
        <g opacity="0.45">
          {[40, 100, 160, 220, 280, 340].map((x) => (
            <rect key={x} x={x} y="105" width="14" height="3" fill={stroke} />
          ))}
        </g>
      </svg>
    );
  }

  /* ───────── ETC GANTRY (highway with ETC lanes) ───────── */
  if (variant === "etc-gantry") {
    return (
      <svg viewBox="0 0 420 220" fill="none" className={className} aria-hidden="true" {...rest}>
        {/* Sky / horizon */}
        <line x1="0" y1="160" x2="420" y2="160" stroke={stroke} strokeWidth="0.8" opacity="0.35" />
        {/* Gantry uprights */}
        <rect x="40" y="40" width="6" height="130" fill={stroke} opacity="0.9" />
        <rect x="374" y="40" width="6" height="130" fill={stroke} opacity="0.9" />
        {/* Gantry crossbar */}
        <rect x="40" y="40" width="340" height="12" fill={stroke} />
        {/* ETC lane signs */}
        <g className="animate-brand-fade-in" style={{ animationDelay: "200ms" }}>
          <rect x="80" y="58" width="80" height="34" rx="2" fill={PAL.purple} />
          <text x="120" y="80" fontFamily="monospace" fontSize="14" fontWeight="700" fill="#FFFFFF" textAnchor="middle" letterSpacing="2">ETC</text>
        </g>
        <g className="animate-brand-fade-in" style={{ animationDelay: "330ms" }}>
          <rect x="170" y="58" width="80" height="34" rx="2" fill={PAL.green} />
          <text x="210" y="80" fontFamily="monospace" fontSize="14" fontWeight="700" fill="#FFFFFF" textAnchor="middle" letterSpacing="2">ETC</text>
        </g>
        <g className="animate-brand-fade-in" style={{ animationDelay: "460ms" }}>
          <rect x="260" y="58" width="80" height="34" rx="2" fill={PAL.blue} />
          <text x="300" y="80" fontFamily="monospace" fontSize="14" fontWeight="700" fill="#FFFFFF" textAnchor="middle" letterSpacing="2">ETC</text>
        </g>
        {/* Lane divider dashed lines */}
        <g stroke={stroke} strokeWidth="1.5" opacity="0.55" strokeDasharray="6 10">
          <line x1="160" y1="105" x2="160" y2="165" />
          <line x1="250" y1="105" x2="250" y2="165" />
        </g>
        {/* Three little cars passing through */}
        <g className="animate-brand-fade-up" style={{ animationDelay: "560ms" }}>
          <rect x="100" y="140" width="40" height="18" rx="3" fill={PAL.purple} />
          <rect x="106" y="134" width="28" height="8" rx="2" fill={PAL.purple} opacity="0.7" />
          <circle cx="108" cy="160" r="3" fill={stroke} />
          <circle cx="132" cy="160" r="3" fill={stroke} />
        </g>
        <g className="animate-brand-fade-up" style={{ animationDelay: "680ms" }}>
          <rect x="190" y="140" width="40" height="18" rx="3" fill={PAL.green} />
          <rect x="196" y="134" width="28" height="8" rx="2" fill={PAL.green} opacity="0.7" />
          <circle cx="198" cy="160" r="3" fill={stroke} />
          <circle cx="222" cy="160" r="3" fill={stroke} />
        </g>
        <g className="animate-brand-fade-up" style={{ animationDelay: "800ms" }}>
          <rect x="280" y="140" width="40" height="18" rx="3" fill={PAL.blue} />
          <rect x="286" y="134" width="28" height="8" rx="2" fill={PAL.blue} opacity="0.7" />
          <circle cx="288" cy="160" r="3" fill={stroke} />
          <circle cx="312" cy="160" r="3" fill={stroke} />
        </g>
      </svg>
    );
  }

  /* ───────── ETC CARD (with chip + animated read beam) ───────── */
  if (variant === "etc-card") {
    return (
      <svg viewBox="0 0 320 200" fill="none" className={className} aria-hidden="true" {...rest}>
        <defs>
          <linearGradient id="etc-card-grad" x1="0" x2="1" y1="0" y2="1">
            <stop offset="0%" stopColor={PAL.navy} />
            <stop offset="100%" stopColor={PAL.purple} />
          </linearGradient>
        </defs>
        {/* Card body */}
        <rect x="20" y="20" width="280" height="160" rx="12" fill="url(#etc-card-grad)" className="animate-brand-scale-in" />
        {/* Chip */}
        <g className="animate-brand-fade-in" style={{ animationDelay: "200ms" }}>
          <rect x="40" y="78" width="40" height="32" rx="4" fill="#E2B763" />
          <g stroke="#9F803F" strokeWidth="0.7" opacity="0.7">
            <line x1="40" y1="86" x2="80" y2="86" />
            <line x1="40" y1="94" x2="80" y2="94" />
            <line x1="40" y1="102" x2="80" y2="102" />
            <line x1="55" y1="78" x2="55" y2="110" />
            <line x1="65" y1="78" x2="65" y2="110" />
          </g>
        </g>
        {/* "ETC" lettering */}
        <text x="40" y="58" fill="#FFFFFF" fontFamily="monospace" fontWeight="700" fontSize="20" letterSpacing="4" className="animate-brand-fade-in" style={{ animationDelay: "300ms" }}>ETC</text>
        {/* Card number placeholder */}
        <g fill="#FFFFFF" opacity="0.85" className="animate-brand-fade-in" style={{ animationDelay: "400ms" }}>
          <rect x="100" y="84" width="38" height="6" rx="1" />
          <rect x="148" y="84" width="38" height="6" rx="1" />
          <rect x="196" y="84" width="38" height="6" rx="1" />
          <rect x="244" y="84" width="38" height="6" rx="1" />
        </g>
        {/* Cardholder + valid thru */}
        <text x="40" y="155" fill="#FFFFFF" opacity="0.85" fontFamily="monospace" fontSize="9" letterSpacing="2">VALID THRU 12/30</text>
        <text x="180" y="155" fill="#FFFFFF" opacity="0.7" fontFamily="monospace" fontSize="9" letterSpacing="2">JAPAN</text>
        {/* Read beam (animated horizontal sweep) */}
        <rect
          x="-30"
          y="20"
          width="60"
          height="160"
          fill="#FFFFFF"
          opacity="0.18"
          className="animate-brand-marquee"
          style={{ animationDuration: "5s" }}
        />
      </svg>
    );
  }

  /* ───────── RECEIPT (drawing-in line items) ───────── */
  if (variant === "receipt") {
    return (
      <svg viewBox="0 0 260 320" fill="none" className={className} aria-hidden="true" {...rest}>
        {/* Receipt paper body with subtle drop shadow */}
        <rect x="20" y="20" width="220" height="280" rx="3" fill="#FFFFFF" stroke={stroke} strokeWidth="0.8" opacity="0.96" />
        {/* Top torn / zigzag */}
        <path d="M20 20 L 32 28 L 44 20 L 56 28 L 68 20 L 80 28 L 92 20 L 104 28 L 116 20 L 128 28 L 140 20 L 152 28 L 164 20 L 176 28 L 188 20 L 200 28 L 212 20 L 224 28 L 236 20 Z" fill="#FFFFFF" stroke={stroke} strokeWidth="0.8" />
        {/* Header */}
        <text x="40" y="58" fill={stroke} fontFamily="monospace" fontWeight="700" fontSize="11" letterSpacing="3">TOLL RECEIPT</text>
        <line x1="40" y1="68" x2="220" y2="68" stroke={stroke} strokeWidth="0.6" opacity="0.4" />
        {/* Line items animating in */}
        {[
          { y: 92, w: 110 },
          { y: 122, w: 90 },
          { y: 152, w: 130 },
          { y: 182, w: 80 },
          { y: 212, w: 100 },
        ].map((row, i) => (
          <g key={i} className="animate-brand-slide-right" style={{ animationDelay: `${i * 180}ms` }}>
            <rect x="40" y={row.y} width={row.w} height="6" rx="1" fill={stroke} opacity="0.7" />
            <rect x={170 + (i % 2) * 8} y={row.y} width="40" height="6" rx="1" fill={stroke} opacity="0.55" />
          </g>
        ))}
        {/* Total row */}
        <line x1="40" y1="244" x2="220" y2="244" stroke={stroke} strokeWidth="0.6" opacity="0.5" />
        <g className="animate-brand-fade-in" style={{ animationDelay: "1100ms" }}>
          <text x="40" y="266" fill={stroke} fontFamily="monospace" fontWeight="700" fontSize="12" letterSpacing="2">TOTAL</text>
          <rect x="160" y="256" width="60" height="12" rx="1" fill={stroke} opacity="0.85" />
        </g>
        {/* Barcode */}
        <g className="animate-brand-fade-in" style={{ animationDelay: "1400ms" }}>
          {[0, 1, 3, 4, 6, 8, 9, 11, 13, 14, 16, 18, 20, 21, 23].map((b, i) => (
            <rect key={i} x={50 + b * 7} y="284" width={i % 3 === 0 ? 3 : 1.5} height="10" fill={stroke} />
          ))}
        </g>
      </svg>
    );
  }

  /* ───────── YEN COIN (rotating coin with orbit) ───────── */
  if (variant === "yen-coin") {
    return (
      <svg viewBox="0 0 220 220" fill="none" className={className} aria-hidden="true" {...rest}>
        {/* Outer orbit ring */}
        <circle cx="110" cy="110" r="98" stroke={stroke} strokeWidth="0.8" opacity="0.25" strokeDasharray="3 6" className="animate-brand-orbit origin-center" style={{ transformOrigin: "110px 110px" }} />
        {/* Coin body */}
        <circle cx="110" cy="110" r="74" fill={stroke} className="animate-brand-scale-in" />
        <circle cx="110" cy="110" r="74" fill="none" stroke="#FFFFFF" strokeWidth="2" opacity="0.45" />
        <circle cx="110" cy="110" r="64" fill="none" stroke="#FFFFFF" strokeWidth="1" opacity="0.35" />
        {/* Yen mark */}
        <g className="animate-brand-fade-in" style={{ animationDelay: "300ms" }}>
          <text x="110" y="138" fill="#FFFFFF" textAnchor="middle" fontFamily="serif" fontWeight="700" fontSize="84" letterSpacing="-2">¥</text>
        </g>
        {/* Orbiting small dot */}
        <g className="animate-brand-orbit origin-center" style={{ transformOrigin: "110px 110px", animationDuration: "18s" }}>
          <circle cx="208" cy="110" r="4" fill={PAL.sky} />
        </g>
      </svg>
    );
  }

  /* ───────── JAPAN REACH MAP (symbolic archipelago + dots) ───────── */
  if (variant === "japan-reach") {
    // Symbolic Japan outline, deliberately abstract — never a precise geographic claim.
    const dots = [
      { cx: 308, cy: 70, r: 4, color: PAL.purple, delay: 0 },     // Hokkaido (Sapporo)
      { cx: 252, cy: 142, r: 4, color: PAL.green, delay: 220 },   // Tohoku (Sendai)
      { cx: 222, cy: 184, r: 5, color: PAL.blue, delay: 380 },    // Kanto (Tokyo / Yokohama / Yokosuka)
      { cx: 198, cy: 198, r: 3.5, color: PAL.purple, delay: 460 },// Tokai (Nagoya)
      { cx: 170, cy: 218, r: 3.5, color: PAL.green, delay: 540 }, // Kansai (Osaka / Kyoto)
      { cx: 122, cy: 234, r: 3.5, color: PAL.blue, delay: 640 },  // Chugoku (Hiroshima)
      { cx: 88, cy: 256, r: 3.5, color: PAL.purple, delay: 740 }, // Kyushu (Fukuoka)
      { cx: 56, cy: 308, r: 3, color: PAL.green, delay: 840 },    // Okinawa
    ];
    return (
      <svg viewBox="0 0 380 360" fill="none" className={className} aria-hidden="true" {...rest}>
        {/* Abstract archipelago — four soft blob landmasses */}
        <g fill={stroke} opacity="0.10">
          {/* Hokkaido */}
          <path d="M 268 36 Q 320 32, 336 64 Q 344 92, 322 102 Q 290 110, 270 94 Q 252 76, 258 56 Z" />
          {/* Honshu (largest) */}
          <path d="M 88 232 Q 110 196, 156 196 Q 200 194, 228 178 Q 256 162, 252 138 Q 244 116, 222 124 Q 198 132, 188 156 Q 168 168, 142 178 Q 110 192, 86 214 Z" />
          {/* Shikoku */}
          <path d="M 132 252 Q 158 244, 170 256 Q 168 268, 148 268 Q 130 266, 132 252 Z" />
          {/* Kyushu */}
          <path d="M 64 244 Q 96 236, 108 254 Q 110 280, 86 282 Q 60 280, 56 264 Z" />
          {/* Okinawa */}
          <ellipse cx="56" cy="308" rx="10" ry="4" />
          <ellipse cx="40" cy="318" rx="6" ry="3" />
        </g>
        {/* Soft regional glow under each dot */}
        {dots.map((d, i) => (
          <circle
            key={`glow-${i}`}
            cx={d.cx}
            cy={d.cy}
            r={d.r + 8}
            fill={d.color}
            opacity="0.14"
            className="animate-brand-pulse-soft"
            style={{ animationDelay: `${d.delay + 500}ms` }}
          />
        ))}
        {/* Faint connecting threads — symbolic, not literal */}
        <g stroke={stroke} strokeWidth="0.7" opacity="0.22" fill="none" strokeDasharray="800">
          <path d="M 222 184 L 308 70" className="animate-brand-draw" style={{ animationDelay: "350ms" }} />
          <path d="M 222 184 L 252 142" className="animate-brand-draw" style={{ animationDelay: "420ms" }} />
          <path d="M 222 184 L 198 198" className="animate-brand-draw" style={{ animationDelay: "470ms" }} />
          <path d="M 222 184 L 170 218" className="animate-brand-draw" style={{ animationDelay: "520ms" }} />
          <path d="M 222 184 L 122 234" className="animate-brand-draw" style={{ animationDelay: "580ms" }} />
          <path d="M 222 184 L 88 256" className="animate-brand-draw" style={{ animationDelay: "640ms" }} />
          <path d="M 222 184 L 56 308" className="animate-brand-draw" style={{ animationDelay: "720ms" }} />
        </g>
        {/* Customer dots */}
        {dots.map((d, i) => (
          <g key={`dot-${i}`} className="animate-brand-fade-in" style={{ animationDelay: `${d.delay}ms` }}>
            <circle cx={d.cx} cy={d.cy} r={d.r} fill={d.color} />
            <circle cx={d.cx} cy={d.cy} r={d.r + 1.5} fill="none" stroke={d.color} strokeWidth="0.5" opacity="0.55" />
          </g>
        ))}
        {/* Center anchor — Yokosuka home */}
        <g className="animate-brand-scale-in" style={{ animationDelay: "200ms" }}>
          <circle cx="222" cy="184" r="9" fill="#FFFFFF" stroke={PAL.navy} strokeWidth="2" />
          <circle cx="222" cy="184" r="3" fill={PAL.navy} />
        </g>
      </svg>
    );
  }

  /* ───────── TATAMI GRID ───────── */  if (variant === "tatami-grid") {
    return (
      <svg viewBox="0 0 600 400" fill="none" className={className} aria-hidden="true" preserveAspectRatio="xMidYMid slice" {...rest}>
        <defs>
          <pattern id="tatami-pat" x="0" y="0" width="120" height="60" patternUnits="userSpaceOnUse">
            <rect x="1" y="1" width="118" height="58" fill="none" stroke={stroke} strokeWidth="0.6" opacity="0.28" />
            <line x1="60" y1="1" x2="60" y2="59" stroke={stroke} strokeWidth="0.4" opacity="0.16" strokeDasharray="2 3" />
          </pattern>
          <radialGradient id="tatami-fade" cx="50%" cy="50%" r="65%">
            <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0" />
            <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0.95" />
          </radialGradient>
        </defs>
        <rect width="600" height="400" fill="url(#tatami-pat)" />
        <rect width="600" height="400" fill="url(#tatami-fade)" />
      </svg>
    );
  }

  // Fallback: invisible placeholder
  return <svg viewBox="0 0 1 1" className={className} aria-hidden="true" {...rest} />;
}
